import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import useSelector from '@hooks/useSelector'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Button, DashboardModule, Table, Text,
} from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import usePublicationSpecialDeadline from '@hooks/usePublicationSpecialDeadline'
import usePublicationSpecialDeadlines from '@hooks/usePublicationSpecialDeadlines'

import formatDate from '@functions/formatDate'

import SpecialDeadlineActions from './SpecialDeadlineActions'

const buildColumns = publicationEditions => [
  {
    title: 'Published',
    cellProps: {
      position: 'sticky',
      left: 0,
      width: '20%',
    },
    dataKey: 'publication_edition_id',
    render: (publication_edition_id) => {
      const edition = publicationEditions[publication_edition_id] || {}

      return (
        <Text variant="ellipsis">
          {edition.publication_date}
        </Text>
      )
    },
  },
  {
    title: 'Title',
    dataKey: 'title',
  },
  {
    title: 'Booking',
    cellProps: {
      key: 'booking',
      width: 140,
    },
    dataKey: 'options',
    render: (options) => {
      const bookingDate = digObject(options, 'deadlines.booking')
      const formattedDate = formatDate(bookingDate, 'dd/MM/yyyy HH:mm', 'hh:mma dd/MM/yyyy')

      return (
        <Text variant="ellipsis">
          {formattedDate || 'N/A'}
        </Text>
      )
    },
  },
  {
    title: 'Artwork',
    cellProps: {
      key: 'artwork',
      width: 140,
    },
    dataKey: 'options',
    render: (options) => {
      const artworkDate = digObject(options, 'deadlines.artwork')
      const formattedDate = formatDate(artworkDate, 'dd/MM/yyyy HH:mm', 'hh:mma dd/MM/yyyy')

      return (
        <Text variant="ellipsis">
          {formattedDate || 'N/A'}
        </Text>
      )
    },
  },
  {
    title: 'OFI',
    cellProps: {
      key: 'ofi',
      width: 140,
    },
    dataKey: 'options',
    render: (options) => {
      const ofiDate = digObject(options, 'deadlines.ofi')
      const formattedDate = formatDate(ofiDate, 'dd/MM/yyyy HH:mm', 'hh:mma dd/MM/yyyy')

      return (
        <Text variant="ellipsis">
          {formattedDate || 'N/A'}
        </Text>
      )
    },
  },
  {
    title: 'Actions',
    cellProps: {
      textAlign: 'center',
      width: 80,
    },
    dataKey: 'id',
    render: id => <SpecialDeadlineActions publicationSpecialDeadline={{ id }} />,
  },
]

const SpecialDeadlines = (props) => {
  const { publicationId } = props

  const filterPayload = useFilters()
  const { pageFilters } = filterPayload

  const specialDeadlinesPayload = usePublicationSpecialDeadlines({
    filters: { publication_id: publicationId, ...pageFilters },
    performHttpRequests: !!publicationId,
  })

  const {
    filteredPublicationSpecialDeadlines, hasFilteredPublicationSpecialDeadlines, loading,
  } = specialDeadlinesPayload

  const {
    callbacks: { addPublicationSpecialDeadline },
  } = usePublicationSpecialDeadline({ publication_id: publicationId })

  const {
    _updates: { publicationEditions: publicationEditionsUpdatedAt },
    publicationEditions,
  } = useSelector(reduxState => reduxState.entities)

  const memoColumns = useMemo(() => (
    buildColumns(publicationEditions)
  ), [filteredPublicationSpecialDeadlines.length, publicationEditionsUpdatedAt])

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-special-deadlines' }}
      contentBoxProps={{ flexDirection: 'column' }}
      loading={loading}
      title="Special Deadlines"
    >
      {!loading && !hasFilteredPublicationSpecialDeadlines && (
        <DashboardModule.BlankState>
          <DashboardModule.BlankState.Title>
            No Special Deadlines Present
          </DashboardModule.BlankState.Title>

          <DashboardModule.BlankState.Paragraph>
            Please add a one below
          </DashboardModule.BlankState.Paragraph>
        </DashboardModule.BlankState>
      )}

      {!loading && hasFilteredPublicationSpecialDeadlines && (
        <Table
          boxProps={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}
          columns={memoColumns}
          data={filteredPublicationSpecialDeadlines}
          scroll={{ x: 800 }}
          stickyHeader
        />
      )}

      <DashboardModule.Footer
        boxProps={{
          borderTop: loading ? 'none' : '1px solid',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          buttonStyle="secondaryEdit"
          icon={<FontAwesomeIcon icon={faPlus} />}
          marginLeft="auto"
          onClick={() => addPublicationSpecialDeadline()}
          size={['medium', 'small']}
          width={['100%', 'fit-content']}
        >
          Add Special Deadline
        </Button>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

SpecialDeadlines.propTypes = {
  publicationId: PropTypes.number.isRequired,
}

export default SpecialDeadlines
